import { PlanningConstraints, PlanningSave, PlanningSaveAcknowledgement } from '@app/core/planning/planning.types';
import { validatePlanningConstraints } from '@app/core/planning/planning-engine';
import { Injectable } from '@angular/core';
import { GanttCellActionType, GanttLink, GanttTask, MachineResource, PartialGanttOptions } from '@app/core/components/gantt/gantt.types';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({ providedIn: 'root' })
export class GanttService {
    constructor(private datatask: DataTaskService) {}

    /** DataTask 4064: schema/options for the registered task provider. */
    public getGanttColumns({ loadDataMethod, parameters }: GanttColumnsParams): Promise<PartialGanttOptions> {
        const source = resolveGanttDataTaskRequest({ method: loadDataMethod, parameters });
        return this.datatask.Post(4064, { ...source.parameters, p2_datatask_keyno: loadDataMethod });
    }

    /** DataTask 4068: generic save; pages configure only their task provider. */
    public async saveGanttData({ loadDataTask, changes, planning }: GanttSaveParams): Promise<GanttSaveResponse> {
        if (!Number.isInteger(loadDataTask.method) || loadDataTask.method <= 0) throw new Error('Gantt saving requires a valid source DataTask.');
        for (const entity of ['task', 'link'] as const) {
            this.validateRowIdentities(changes.filter(change => change.entity === entity), entity);
        }
        if (changes.some(change => String(change._uuid) !== String(change.row['_uuid']))) throw new Error('Gantt save row _uuid must match its change identity.');
        if (changes.some(change => !['forward', 'backward'].includes(change.planningDirection))) throw new Error('Each Gantt change requires a forward or backward planning direction.');
        loadDataTask = resolveGanttDataTaskRequest(loadDataTask);
        const response: GanttSaveResponse = await this.datatask.Post(4068, {
            ...loadDataTask.parameters,
            p2_datatask_keyno: loadDataTask.method,
            datatask: loadDataTask,
            changes,
            ...(planning ? { planning } : {}),
        }, { returnRawResponse: true });
        if (!response || typeof response.errorcode !== 'number' || typeof response.errormessage !== 'string') throw new Error('Gantt save returned an invalid response.');
        if (response.errorcode !== 0) throw new GanttSaveError(response.errormessage || 'The changes could not be saved.', response.errorcode === 99580 ? 'planning_save_stale' : undefined, response.errorcode === 99580);
        if (planning) {
            const ack = response.savedata?.planning;
            if (!ack || ack.version !== 1 || typeof ack.snapshot_id !== 'string' || !ack.snapshot_id.trim()) {
                throw new GanttSaveError('The server did not confirm the saved plan. Reload before saving again.', 'planning_save_unconfirmed', true);
            }
        }
        return response;
    }

    /** DataTask 4067: resolve the clicked row's type, state and params. */
    public async getGotoParms({ loadDataTask, field, row, rememberId, trigger = 'click', area }: GanttGotoParmsParams): Promise<GanttGotoParmsResponse> {
        if (loadDataTask) loadDataTask = resolveGanttDataTaskRequest(loadDataTask);
        const response: GanttGotoParmsResponse[] = await this.datatask.Post(4067, {
            // Expose the provider business parameters to its navigation procedure.
            ...loadDataTask?.parameters,
            p2_datatask_keyno: loadDataTask?.method,
            datatask: loadDataTask, field, row, trigger,
            ...(area ? { area } : {}),
            // Preserve the shared SQL navigation protocol's remember-id name.
            gridRemId: rememberId,
        });
        if (!Array.isArray(response)) throw new Error('Gantt navigation must return a result array.');
        const target = response[0] ?? { type: 'default', state: '', params: '' };
        if (typeof target.state !== 'string' || typeof target.params !== 'string') throw new Error('Gantt navigation must return state and parameters as strings.');
        if (!['default', 'modal', 'popup'].includes(target.type)) throw new Error('Gantt navigation type must be default, modal or popup.');
        return target;
    }

    /** DataTask 4065: tasks, links and resources for the same registered provider. */
    public async getGanttData(request: GanttDataTaskRequest): Promise<GanttDataGet> {
        const { method, parameters } = resolveGanttDataTaskRequest(request);
        // Keep links/resources beside data; the default DataTask response unwraps data alone.
        const response: GanttDataGet = await this.datatask.Post(4065, { ...parameters, p2_datatask_keyno: method }, { returnRawResponse: true });
        if (!response || !Array.isArray(response.data)) throw new Error('Gantt data must contain a task array.');
        if (!Array.isArray(response.links)) throw new Error('Gantt links must be an array.');
        if (response.resources !== undefined && !Array.isArray(response.resources)) throw new Error('Gantt resources must be an array.');
        this.validateRowIdentities(response.data, 'task');
        this.validateRowIdentities(response.links, 'link');
        if (response.constraints != null) validatePlanningConstraints(response.constraints);
        return { data: response.data, links: response.links, resources: response.resources, ...(response.constraints != null ? { constraints: response.constraints } : {}) };
    }

    private validateRowIdentities(rows: { _uuid?: unknown }[], entity: 'task' | 'link'): void {
        const seen = new Set<string>();
        for (const row of rows) {
            const uuid = row?._uuid;
            if ((typeof uuid !== 'string' && typeof uuid !== 'number') || String(uuid).trim() === '' || (typeof uuid === 'number' && !Number.isFinite(uuid))) {
                throw new Error('Each Gantt ' + entity + ' row requires a nonempty _uuid.');
            }
            const key = String(uuid);
            if (seen.has(key)) throw new Error('Gantt ' + entity + ' rows must have unique _uuid values.');
            seen.add(key);
        }
    }
}

export type GanttDataTaskParameters = object | (() => object | undefined);

/** Evaluate callbacks once so schema and data can share a request snapshot. */
export function resolveGanttDataTaskRequest(request: GanttDataTaskRequest): { method: number; parameters?: object } {
    return { ...request, parameters: typeof request.parameters === 'function' ? request.parameters() : request.parameters };
}

export interface GanttDataTaskRequest {
    /** Registered task-provider DataTask number; the generic loader is called automatically. */
    method: number;
    parameters?: GanttDataTaskParameters;
}

export interface GanttDataTask {
    loadData: GanttDataTaskRequest;
}

export interface GanttColumnsParams {
    loadDataMethod: number;
    parameters?: GanttDataTaskParameters;
}

export interface GanttDataGet {
    data: GanttTask[];
    links: GanttLink[];
    resources?: MachineResource[];
    /** Optional provider-neutral allocation context, discovered by the generic loader. */
    constraints?: PlanningConstraints | null;
}

export interface GanttGotoParmsParams {
    loadDataTask?: GanttDataTaskRequest;
    /** Bare column name; the response chooses how its destination opens. */
    field: string;
    row: GanttTask | MachineResource;
    /** Distinguishes task grid, task bar and resource grid navigation. */
    area?: GanttNavigationArea;
    rememberId?: string;
    trigger?: GanttActionTrigger;
}

export type GanttActionTrigger = 'click' | 'dblclick';
export type GanttNavigationArea = 'grid' | 'timeline' | 'resource';

export interface GanttGotoParmsResponse {
    type: GanttCellActionType;
    state: string;
    params: string;
}

export interface GanttSaveParams {
    loadDataTask: GanttDataTaskRequest;
    changes: GanttSaveChange[];
    planning?: PlanningSave;
}

export interface GanttSaveChange {
    /** Provider row key or the UUID assigned when the frontend creates a row. */
    _uuid: string | number;
    id: string | number;
    entity: 'task' | 'link';
    event: 'autoschedule' | 'drag' | 'move' | 'update' | 'add' | 'delete';
    /** Strategy captured when this change was made, independent of later toolbar toggles. */
    planningDirection: GanttPlanningDirection;
    parentId: string | number | null;
    row: Record<string, unknown> & { _uuid: string | number };
}

export interface GanttSaveResponse {
    errorcode: number;
    errormessage: string;
    read?: boolean | number;
    savedata?: { planning?: PlanningSaveAcknowledgement };
}

export class GanttSaveError extends Error {
    constructor(message: string, public readonly messageKey?: string, public readonly requiresReload = false) { super(message); }
}

export type GanttPlanningDirection = 'forward' | 'backward';
